/**
 * useUpgradeMarketplaceApp Hook
 *
 * React Query mutation hook for upgrading marketplace applications.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { tenantMarketplaceService } from '../../../api/marketplace.service';
import type { MarketplaceUpgradeDTO } from '../../../types/marketplace.types';
import type { ApplicationSanitized } from '../../../types/application.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UpgradeAppParams {
  tenantId: string;
  applicationId: string;
  data?: MarketplaceUpgradeDTO;
}

/**
 * Hook for upgrading an installed marketplace application
 */
export const useUpgradeMarketplaceApp = (): UseMutationResult<
  ApplicationSanitized,
  Error,
  UpgradeAppParams
> => {
  const queryClient = useQueryClient();

  return useMutation<ApplicationSanitized, Error, UpgradeAppParams>({
    mutationFn: async ({ tenantId, applicationId, data }) => {
      console.log('[useUpgradeMarketplaceApp] Upgrading app:', applicationId);
      return await tenantMarketplaceService.upgradeApp(tenantId, applicationId, data);
    },
    onSuccess: (_, { tenantId }) => {
      // Invalidate applications list
      queryClient.invalidateQueries({ queryKey: ['applications', tenantId] });
      // Invalidate installed marketplace apps
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'installed', tenantId] });
      // Invalidate updates check
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'updates', tenantId] });
    },
    onError: (error) => {
      console.error('[useUpgradeMarketplaceApp] Upgrade failed:', getErrorMessage(error));
    },
  });
};
